import React, {Component, createRef} from 'react'
import PropTypes from 'prop-types'
import {Prompt} from 'react-router-dom'
import {Skeleton} from 'antd'
import {v4 as uuidV4} from 'uuid'
import PubSub from 'pubsub-js'
import LayerTable from './LayerTable'
import ConfirmActionModal from './ConfirmActionModal'
import Toast from './Toast'
import {MAX_LAYERS} from './constants'
import {updateSkuLayerConfig, getListMaterials} from '../../../../services/api/apiSku'

const CONFIRM_DELETE_LAYER = 'DELETE_LAYER'
const CONFIRM_TURN_OFF = 'TURN_OFF'
const TOAST_DURATION = 3000

const SKELETON_ROWS = [1, 2, 3]

const LEAVE_WARNING = 'Khối Cấu trúc sản xuất còn thay đổi chưa lưu. Rời trang sẽ mất các thay đổi này.'

const WHITE_PRINT_TIP = 'Đánh dấu nếu SKU cần in lớp mực trắng khi sản xuất. Giá trị này độc lập với Phương pháp sản xuất — đổi phương pháp không làm thay đổi ô này.'

const makeLayer = (layer = {}) => ({
    key: uuidV4(),
    material_code: layer.material_code || '',
    material_name: layer.material_name || '',
    description: layer.description || '',
    is_print: !!layer.is_print,
    is_cut: !!layer.is_cut,
})

class ProductionStructure extends Component {
    state = {
        loading: false,
        loadFailed: false,
        saving: false,
        error: '',
        materials: [],
        materialsLoaded: false,
        materialsError: '',
        hasLayerConfig: false,
        whitePrint: false,
        layers: [],
        errors: {},
        snapshot: '',
        appliedConfig: null,
        isLoaded: false,
        focusKey: '',
        confirm: null,
        toast: null,
    }

    _rootRef = createRef()
    _toastTimer = null
    _materialsRequested = false
    _isMounted = false
    /** Cha có thể đang bay một GET /sku/:id cũ hơn lần lưu này -> bỏ qua đúng một config kế tiếp. */
    _skipNextConfig = false

    componentDidMount() {
        this._isMounted = true
        window.addEventListener('beforeunload', this._handleBeforeUnload)
        this._getLayerConfig()
    }

    componentDidUpdate(prevProps) {
        const {config, loading} = this.props

        // Trang cha chạy xong mà vẫn không có config -> nạp thất bại.
        // Không thoát skeleton ở đây thì khối quay vô tận vì config giữ nguyên null.
        if (prevProps.loading && !loading && !config && !this.state.isLoaded) {
            return this.setState({loading: false, loadFailed: true, error: 'Không tải được cấu hình cấu trúc sản xuất.'})
        }

        if (prevProps.config === config) return

        // Đang lưu, hoặc vừa lưu xong: config từ cha có thể là bản chụp TRƯỚC khi lưu.
        if (this.state.saving) return

        if (this._skipNextConfig) {
            this._skipNextConfig = false
            return
        }

        // Khối lưu độc lập với Thông tin chung: khi trang cha nạp lại SKU mà người dùng
        // đang sửa dở ở đây thì giữ nguyên, không đè mất thay đổi chưa lưu.
        if (this.state.isLoaded && this._isDirty()) return

        this._getLayerConfig()
    }

    componentWillUnmount() {
        this._isMounted = false
        window.removeEventListener('beforeunload', this._handleBeforeUnload)

        if (this._toastTimer) clearTimeout(this._toastTimer)
    }

    /** setState sau khi rời trang chỉ tổ log warning — chặn ở đây cho gọn. */
    _setState = (...args) => {
        if (this._isMounted) this.setState(...args)
    }

    _handleBeforeUnload = (e) => {
        if (!this._isDirty()) return

        e.preventDefault()
        e.returnValue = LEAVE_WARNING

        return LEAVE_WARNING
    }

    _toast = (text, type = '') => {
        if (this._toastTimer) clearTimeout(this._toastTimer)

        this._setState({toast: {text, type}})
        this._toastTimer = setTimeout(() => this._setState({toast: null}), TOAST_DURATION)
    }

    /** Dùng luôn cờ loading của trang cha — nó bật suốt thời gian _getDetailSku chạy. */
    _isLoading = () => this.state.loading || !!this.props.loading

    /** Bỏ material_name khỏi snapshot: tên BE trả về có thể khác tên trong danh mục NVL. */
    _makeSnapshot = (hasLayerConfig, whitePrint, layers) => JSON.stringify({
        hasLayerConfig,
        whitePrint,
        layers: layers.map(({material_code, description, is_print, is_cut}) => ({
            material_code, description, is_print, is_cut,
        })),
    })

    _isDirty = () => {
        const {isLoaded, hasLayerConfig, whitePrint, layers, snapshot} = this.state

        // Chưa nạp xong lần đầu thì chưa có gì để so — snapshot rỗng không phải là "đã sửa".
        if (!isLoaded) return false

        return this._makeSnapshot(hasLayerConfig, whitePrint, layers) !== snapshot
    }

    /** Chỉ kéo danh mục NVL khi khối thực sự cần, tránh thêm request cho mọi lượt mở SKU. */
    _ensureMaterials = () => {
        if (this._materialsRequested) return

        this._materialsRequested = true
        this._getMaterials()
    }

    _handleRetryMaterials = () => {
        this._materialsRequested = true
        this._getMaterials()
    }

    _getMaterials = async () => {
        this._setState({materialsError: ''})

        try {
            const {data: response} = await getListMaterials({page: 1, limit: 10000})
            const {data, success, message: error} = Object.assign({}, response)

            if (!success) {
                this._materialsRequested = false
                return this._setState({materialsError: error || 'Không tải được danh mục nguyên vật liệu.'})
            }

            this._setState({materials: (data && data.data) || [], materialsLoaded: true})
        } catch (e) {
            // Nuốt lỗi ở đây thì mọi layer bị gắn nhãn sai "NVL không còn trong danh mục".
            this._materialsRequested = false
            this._setState({materialsError: e.message})
        }
    }

    _applyConfig = (config) => {
        const {has_layer_config = false, white_print = false, layers = []} = Object.assign({}, config)
        const sortedLayers = [...layers]
            .sort((a, b) => (a.layer_no || 0) - (b.layer_no || 0))
            .map(makeLayer)

        if (has_layer_config) this._ensureMaterials()

        this._setState({
            loading: false,
            loadFailed: false,
            isLoaded: true,
            error: '',
            hasLayerConfig: !!has_layer_config,
            whitePrint: !!white_print,
            layers: sortedLayers,
            errors: {},
            focusKey: '',
            appliedConfig: config,
            snapshot: this._makeSnapshot(!!has_layer_config, !!white_print, sortedLayers),
        })
    }

    _getLayerConfig = () => {
        // Trang cha nạp SKU bất đồng bộ: lúc mount config còn null, giữ loading
        // thay vì loé hộp "SKU này không cấu hình cấu trúc layer" rồi mới đổi.
        if (!this.props.config) return this._setState({loading: true, error: ''})

        this._applyConfig(this.props.config)
    }

    _isToggleDisabled = () => {
        const {skuID, isSkuSaved} = this.props
        const {saving, isLoaded} = this.state

        return !skuID || !isSkuSaved || saving || !isLoaded || this._isLoading()
    }

    _handleToggleConfig = () => {
        const {hasLayerConfig, layers} = this.state

        if (this._isToggleDisabled()) return

        if (hasLayerConfig && layers.length > 0) {
            return this.setState({confirm: {type: CONFIRM_TURN_OFF}})
        }

        if (!hasLayerConfig) this._ensureMaterials()

        this.setState({hasLayerConfig: !hasLayerConfig, errors: {}})
    }

    _handleToggleKeyDown = (e) => {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return

        e.preventDefault()
        this._handleToggleConfig()
    }

    _handleChangeWhitePrint = (e) => {
        this.setState({whitePrint: e.target.checked})
    }

    _handleAddLayer = () => {
        const {layers} = this.state

        if (layers.length >= MAX_LAYERS) {
            return this._toast(`Chỉ được cấu hình tối đa ${MAX_LAYERS} layer.`, 'err')
        }

        const newLayer = makeLayer()

        this.setState({layers: [...layers, newLayer], focusKey: newLayer.key})
    }

    _updateLayer = (key, patch, clearError) => {
        const {layers, errors} = this.state
        const current = errors[key]
        const newErrors = Object.assign({}, errors)

        if (current) {
            const rest = Object.assign({}, current)
            delete rest[clearError]

            if (Object.keys(rest).length) newErrors[key] = rest
            else delete newErrors[key]
        }

        this.setState({
            layers: layers.map(layer => (layer.key === key ? {...layer, ...patch} : layer)),
            errors: newErrors,
        })
    }

    _handleChangeMaterial = (key, materialCode) => {
        const picked = this.state.materials.find(material => material.code === materialCode)

        this._updateLayer(key, {
            material_code: materialCode,
            material_name: picked ? picked.name : '',
        }, 'material')
    }

    _handleChangeDescription = (key, description) => {
        this._updateLayer(key, {description})
    }

    _handleChangeFlag = (key, field, checked) => {
        this._updateLayer(key, {[field]: checked}, 'flags')
    }

    _handleReorderLayer = (fromIndex, toIndex) => {
        const layers = [...this.state.layers]
        const [moved] = layers.splice(fromIndex, 1)

        layers.splice(toIndex, 0, moved)

        this.setState({layers})
    }

    _handleRequestDeleteLayer = (index) => {
        this.setState({confirm: {type: CONFIRM_DELETE_LAYER, index}})
    }

    _handleCloseConfirm = () => {
        this.setState({confirm: null})
    }

    _handleConfirm = () => {
        const {confirm, layers, errors} = this.state

        if (!confirm) return

        if (confirm.type === CONFIRM_DELETE_LAYER) {
            const deleted = layers[confirm.index]
            const newErrors = Object.assign({}, errors)

            if (deleted) delete newErrors[deleted.key]

            return this.setState({
                layers: layers.filter((_, index) => index !== confirm.index),
                errors: newErrors,
                confirm: null,
            })
        }

        this.setState({
            hasLayerConfig: false,
            whitePrint: false,
            layers: [],
            errors: {},
            confirm: null,
        })
    }

    _scrollTo = (selector) => {
        const root = this._rootRef.current

        if (!root) return

        const target = root.querySelector(selector)

        if (target) target.scrollIntoView({behavior: 'smooth', block: 'center'})
    }

    _validate = () => {
        const {hasLayerConfig, layers} = this.state

        if (!hasLayerConfig) return {isValid: true, errors: {}}

        if (layers.length === 0) return {isValid: false, errors: {}, isEmpty: true}

        // Chỉ đánh dấu ô nào sai, không lưu sẵn câu chữ: message có nhúng số layer nên
        // phải dựng lúc render theo index hiện tại, không thì kéo thả/xoá dòng là lệch.
        const errors = layers.reduce((acc, layer) => {
            const layerErrors = {}

            if (!layer.material_code) layerErrors.material = true
            if (!layer.is_print && !layer.is_cut) layerErrors.flags = true

            if (Object.keys(layerErrors).length) acc[layer.key] = layerErrors

            return acc
        }, {})

        return {isValid: Object.keys(errors).length === 0, errors}
    }

    _handleSave = async () => {
        const {skuID} = this.props
        const {hasLayerConfig, whitePrint, layers} = this.state
        const {isValid, errors, isEmpty} = this._validate()

        this.setState({errors})

        if (isEmpty) {
            this._toast('Cấu hình đang bật nhưng chưa có layer nào — thêm layer hoặc tắt cấu hình', 'err')
            return this._scrollTo('.ps-empty')
        }

        if (!isValid) {
            this._toast('Không lưu được cấu trúc sản xuất — kiểm tra các dòng đang báo lỗi', 'err')
            return setTimeout(() => this._scrollTo('.ps-table .is-err'), 0)
        }

        this.setState({saving: true, error: ''})

        try {
            const payload = {
                has_layer_config: hasLayerConfig,
                white_print: hasLayerConfig ? whitePrint : false,
                // BE tự đánh layer_no 1..n theo thứ tự mảng nên không gửi kèm.
                layers: hasLayerConfig
                    ? layers.map(({material_code, description, is_print, is_cut}) => ({
                        material_code, description, is_print, is_cut,
                    }))
                    : [],
            }

            const {data: response} = await updateSkuLayerConfig(skuID, payload)
            const {success, data, message: error} = Object.assign({}, response)

            if (!success) {
                this._setState({saving: false})
                return this._toast(error || 'Lưu cấu trúc sản xuất không thành công.', 'err')
            }

            this._skipNextConfig = true
            this._setState({saving: false})
            this._toast(hasLayerConfig ? 'Đã lưu cấu trúc sản xuất' : 'Đã lưu — SKU không cấu hình cấu trúc layer')
            PubSub.publish('GET_HISTORY')

            // Response trả về cấu hình đã lưu (kèm layer_no BE đánh) -> áp thẳng, không cần gọi lại API.
            // BE không trả body thì dùng chính cái vừa gửi, tránh reset khối về "chưa cấu hình".
            const isConfigResponse = !!data && typeof data === 'object' && 'has_layer_config' in data

            this._applyConfig(isConfigResponse ? data : {
                ...payload,
                layers: payload.layers.map((layer, index) => ({
                    ...layer,
                    layer_no: index + 1,
                    material_name: layers[index] ? layers[index].material_name : '',
                })),
            })
        } catch (e) {
            this._setState({saving: false, error: e.message})
            this._toast(e.message, 'err')
        }
    }

    _handleCancel = () => {
        const {appliedConfig, isLoaded} = this.state

        if (!isLoaded) return

        // Áp lại nguyên cấu hình đã nạp: khôi phục cả material_name, thứ mà snapshot không giữ.
        this._applyConfig(appliedConfig)
        this._toast('Đã hoàn tác thay đổi của khối Cấu trúc sản xuất')
    }

    _renderBody = (isInitialLoading) => {
        const {
            saving, isLoaded, error, hasLayerConfig, whitePrint,
            layers, materials, materialsLoaded, errors, focusKey,
        } = this.state

        // Skeleton dựng theo đúng hình dạng khối thật để lúc dữ liệu về không nhảy layout.
        // Chỉ dùng cho lần nạp ĐẦU: cha refetch vì việc khác thì giữ nguyên bảng, đỡ nháy.
        if (isInitialLoading) {
            return (
                <div className="ps-skeleton">
                    <div className="ps-wp-bar">
                        <Skeleton.Input active size="small" className="ps-skeleton-wp"/>
                    </div>
                    <div className="ps-skeleton-table">
                        {SKELETON_ROWS.map(row => (
                            <div className="ps-skeleton-row" key={row}>
                                <Skeleton.Input active size="large" className="ps-skeleton-select"/>
                            </div>
                        ))}
                    </div>
                </div>
            )
        }

        if (!isLoaded) {
            return (
                <div className="ps-offbox">
                    <b>{error || 'Không tải được cấu hình cấu trúc sản xuất.'}</b><br/>
                    Tải lại trang để thử lại.
                </div>
            )
        }

        if (!hasLayerConfig) {
            return (
                <div className="ps-offbox">
                    <b>SKU này không cấu hình cấu trúc layer.</b><br/>
                    Áp dụng cho sản phẩm đã có sẵn phôi và xuất thành phẩm luôn, không qua công đoạn in màu.
                    Bật công tắc phía trên nếu cần cấu hình.
                </div>
            )
        }

        return (
            <>
                <div className="ps-wp-bar">
                    <label className="ps-chk">
                        <input
                            type="checkbox"
                            checked={whitePrint}
                            disabled={saving}
                            onChange={this._handleChangeWhitePrint}
                        />
                        <b>In mặt trắng</b>
                    </label>
                    <span className="ps-tip" data-tip={WHITE_PRINT_TIP}>?</span>
                </div>

                <LayerTable
                    layers={layers}
                    materials={materials}
                    materialsLoaded={materialsLoaded}
                    errors={errors}
                    disabled={saving}
                    focusKey={focusKey}
                    onAdd={this._handleAddLayer}
                    onChangeMaterial={this._handleChangeMaterial}
                    onChangeDescription={this._handleChangeDescription}
                    onChangeFlag={this._handleChangeFlag}
                    onRequestDelete={this._handleRequestDeleteLayer}
                    onReorder={this._handleReorderLayer}
                />
            </>
        )
    }

    _renderConfirmModal = () => {
        const {confirm, layers} = this.state

        if (!confirm) return null

        if (confirm.type === CONFIRM_DELETE_LAYER) {
            return (
                <ConfirmActionModal
                    title={`Xoá layer ${confirm.index + 1}?`}
                    content='Layer này sẽ bị xoá khỏi cấu hình. Thay đổi chỉ có hiệu lực sau khi bấm "Lưu cấu trúc sản xuất".'
                    okText="Xoá"
                    onOk={this._handleConfirm}
                    onCancel={this._handleCloseConfirm}
                />
            )
        }

        return (
            <ConfirmActionModal
                title="Tắt cấu hình cấu trúc layer?"
                content={`SKU đang có ${layers.length} layer. Tắt cấu hình sẽ xoá toàn bộ layer và bỏ cờ In mặt trắng. Thay đổi chỉ có hiệu lực sau khi bấm "Lưu cấu trúc sản xuất".`}
                okText="Tắt"
                onOk={this._handleConfirm}
                onCancel={this._handleCloseConfirm}
            />
        )
    }

    render() {
        const {skuID, isSkuSaved} = this.props
        const {saving, error, isLoaded, loadFailed, materialsError, hasLayerConfig, layers, toast} = this.state
        const loading = this._isLoading()
        const isInitialLoading = loading && !isLoaded
        // Nạp lỗi thì state sku của cha vẫn rỗng -> không được rơi vào lockbox,
        // nếu không người dùng đọc nhầm là SKU chưa lưu và đi bấm Cập nhật.
        const isLocked = !isInitialLoading && !loadFailed && !isLoaded && (!skuID || !isSkuSaved)
        const isOn = !isLocked && !isInitialLoading && isLoaded && hasLayerConfig
        const isDirty = this._isDirty()
        // Cha đang nạp lại SKU thì không cho lưu: PUT có thể về trước GET và bị ghi đè ngược.
        const isBusy = saving || loading
        const isToggleDisabled = this._isToggleDisabled()

        return (
            <div className="ProductionStructure" ref={this._rootRef}>
                <Prompt when={isDirty} message={LEAVE_WARNING}/>

                <div className="ps-head">
                    <div className="ps-head-left">
                        <h2>Cấu trúc sản xuất</h2>
                        {isOn && <span className="ps-badge">{layers.length} layer</span>}
                    </div>
                    <div className="ps-head-right">
                        <span className="ps-toggle-label" id="ps-toggle-label">Cấu hình cấu trúc layer:</span>
                        <span
                            className={`ps-switch ${hasLayerConfig ? 'on' : 'off'} ${isToggleDisabled ? 'is-disabled' : ''}`}
                            role="switch"
                            aria-checked={hasLayerConfig}
                            aria-labelledby="ps-toggle-label"
                            aria-disabled={isToggleDisabled}
                            tabIndex={isToggleDisabled ? -1 : 0}
                            onClick={this._handleToggleConfig}
                            onKeyDown={this._handleToggleKeyDown}
                        >
                            <i/>
                            <span>{hasLayerConfig ? 'On' : 'Off'}</span>
                        </span>
                        {isOn && (
                            <button
                                type="button"
                                className="ps-btn ps-btn-primary"
                                disabled={saving || layers.length >= MAX_LAYERS}
                                title={layers.length >= MAX_LAYERS ? `Giới hạn tối đa ${MAX_LAYERS} layer` : ''}
                                onClick={this._handleAddLayer}
                            >
                                + Thêm layer
                            </button>
                        )}
                    </div>
                </div>

                <p className="ps-caption">
                    Bật khi SKU cần in màu để sản xuất. Mỗi layer tương ứng một mặt in màu — một tấm nguyên vật liệu in
                    2 mặt được khai thành 2 layer cùng mã. Mỗi layer phải được đánh dấu <b>In</b>, <b>Cắt</b>, hoặc cả
                    hai. Diện tích in và đường cắt do tool gen file in tự xác định từ hình design thực tế. Tắt với SKU
                    đã có sẵn phôi và xuất thành phẩm luôn.
                </p>

                {!!error && isLoaded && <div className="ps-error">{error}</div>}
                {!!materialsError && (
                    <div className="ps-error">
                        Không tải được danh mục nguyên vật liệu: {materialsError}
                        <button type="button" className="ps-link-btn ml-2" onClick={this._handleRetryMaterials}>
                            Thử lại
                        </button>
                    </div>
                )}

                {isLocked ? (
                    <div className="ps-lockbox">
                        <b>Lưu sản phẩm trước khi cấu hình cấu trúc sản xuất.</b><br/>
                        Hoàn tất phần <i>Thông tin chung</i> và bấm <b>Cập nhật</b> ở cuối trang, sau đó quay lại cấu
                        hình khối này.
                    </div>
                ) : (
                    <>
                        {this._renderBody(isInitialLoading)}

                        {isLoaded && (
                            <div className="ps-blockbar">
                                <span className="ps-hint">
                                    {isDirty && <><i className="ps-dot"/><b>Có thay đổi chưa lưu trong khối này. </b></>}
                                    Khối này lưu độc lập với <i>Thông tin chung</i>.
                                </span>
                                <div className="ps-blockbar-actions">
                                    {/* Chưa sửa gì thì không có gì để hoàn tác -> ẩn hẳn nút Huỷ. */}
                                    {isDirty && (
                                        <button
                                            type="button"
                                            className="ps-btn ps-btn-ghost"
                                            disabled={isBusy}
                                            onClick={this._handleCancel}
                                        >
                                            Huỷ
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="ps-btn ps-btn-primary"
                                        disabled={isBusy || !isDirty}
                                        onClick={this._handleSave}
                                    >
                                        {saving ? 'Đang lưu…' : 'Lưu cấu trúc sản xuất'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {this._renderConfirmModal()}
                <Toast toast={toast}/>
            </div>
        )
    }
}

ProductionStructure.defaultProps = {
    isSkuSaved: true,
}

ProductionStructure.propTypes = {
    skuID: PropTypes.string,
    isSkuSaved: PropTypes.bool,
    config: PropTypes.object,
    loading: PropTypes.bool,
}

export default ProductionStructure
